import { Component, OnInit, ChangeDetectionStrategy, ViewChild, TemplateRef, ChangeDetectorRef, Injectable } from '@angular/core';
import { CalendarEvent, CalendarView, CalendarDateFormatter, DateFormatterParams } from 'angular-calendar';
import { startOfDay, endOfDay, subDays, addDays, endOfMonth, isSameDay, isSameMonth, addHours, format } from 'date-fns';
import { Subject } from 'rxjs';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { NbSidebarService, NbDialogService, NbToastrService } from '@nebular/theme';
import { CampaignService, ScheduledCampaign } from 'src/app/deebr/service/campaign.service';
import { EntrepriseService } from 'src/app/deebr/service/entreprise.service';
import { CategoryService } from 'src/app/deebr/service/category.service';
import { CampaignSidebarComponent } from './campaign-sidebar.component';
import { OptimizeDialogComponent } from './optimize-dialog.component';
import { TourService, type TourStep } from 'src/app/deebr/service/tour.service';

// Define CustomDateFormatter inline to ensure it's used and bypass any cache/file issues
@Injectable()
export class CustomDateFormatter extends CalendarDateFormatter {
    public override monthViewColumnHeader({ date, locale }: DateFormatterParams): string {
        console.log('Using Inline CustomDateFormatter (Month Col)', date);
        return new Intl.DateTimeFormat('fr-FR', { weekday: 'short' }).format(date);
    }

    public override monthViewTitle({ date, locale }: DateFormatterParams): string {
        console.log('Using Inline CustomDateFormatter (Month Title)', date);
        return new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' }).format(date);
    }

    public override weekViewColumnHeader({ date, locale }: DateFormatterParams): string {
        return new Intl.DateTimeFormat('fr-FR', { weekday: 'short' }).format(date);
    }

    public override weekViewTitle({ date, locale }: DateFormatterParams): string {
        const d = date.getDay();
        const monday = new Date(date);
        monday.setDate(date.getDate() - (d === 0 ? 6 : d - 1));
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);

        const fmtMonth = new Intl.DateTimeFormat('fr-FR', { month: 'long' });
        const fmtYear = new Intl.DateTimeFormat('fr-FR', { year: 'numeric' });
        const monMonth = fmtMonth.format(monday);
        const year = fmtYear.format(monday);

        // Capitalize first letter
        const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

        if (monday.getMonth() === sunday.getMonth()) {
            return `${cap(monMonth)} ${year}`;
        }
        // Abbreviated months when spanning two months
        const fmtShort = new Intl.DateTimeFormat('fr-FR', { month: 'short' });
        return `${cap(fmtShort.format(monday))} – ${cap(fmtShort.format(sunday))} ${year}`;
    }

    public override dayViewHour({ date, locale }: DateFormatterParams): string {
        return new Intl.DateTimeFormat('fr-FR', { hour: 'numeric', minute: 'numeric' }).format(date);
    }

    public override dayViewTitle({ date, locale }: DateFormatterParams): string {
        return new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(date);
    }
}

// Deebr brand palette — cycled across campaigns for visual variety
const DEEBR_COLORS = [
    { primary: '#ff6384', secondary: '#ffe0e6' },
    { primary: '#35a2eb', secondary: '#d6edff' },
    { primary: '#74cdcd', secondary: '#ddf4f4' },
    { primary: '#ffd877', secondary: '#fff4d9' },
];



@Component({
    selector: 'app-planning-campaign',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './planning-campaign.component.html',
    styleUrls: ['./planning-campaign.component.scss'],
    providers: [
        {
            provide: CalendarDateFormatter,
            useClass: CustomDateFormatter
        }
    ],
    standalone: false
})
export class PlanningCampaignComponent implements OnInit {
    view: CalendarView = CalendarView.Week;
    CalendarView = CalendarView;
    viewDate: Date = new Date();

    events: CalendarEvent[] = [];
    selectedCampaignIds: Set<number> = new Set();
    isDownloading = false;
    isDeleting = false;

    refresh = new Subject<void>();

    activeDayIsOpen: boolean = false;

    selectedDate: Date = new Date();
    selectedCampaign: ScheduledCampaign | null = null;
    sidebarOpen = false;

    @ViewChild(CampaignSidebarComponent) campaignSidebarRef?: CampaignSidebarComponent;
    @ViewChild('unsavedChangesDialog') unsavedChangesDialog!: TemplateRef<any>;
    @ViewChild('deleteConfirmDialog') deleteConfirmDialog!: TemplateRef<any>;

    weekDays: Date[] = [];
    metaCategories: any[] = [];
    planningCategoriesMap: any = {};
    allCategories: any[] = [];

    // Hover highlight: ID of the card under the mouse, + pre-computed linked IDs
    hoveredEventId: number | null = null;
    hoveredLinkedIds: Set<number> = new Set();

    // Track drag state to suppress hover effects during drag
    isDragging = false;

    private static readonly TOUR_KEY = 'deebr_tour_planning_seen';

    // true si l'entreprise pousse ses segments directement dans l'outil mailing (download_segment = 'no')
    pushMode = false;

    constructor(
        private sidebarService: NbSidebarService,
        private campaignService: CampaignService,
        private categoryService: CategoryService,
        private cdr: ChangeDetectorRef,
        private dialogService: NbDialogService,
        private tourService: TourService,
        private entrepriseService: EntrepriseService,
        private toastrService: NbToastrService
    ) { }

    ngOnInit(): void {
        this.loadCategories(); // Load categories first
        this.updateWeekDays();
        this.autoStartTour();
        this.entrepriseService.getEntreprise().subscribe({
            next: (entreprise) => {
                this.pushMode = entreprise.results[0]?.download_segment === 'no';
                this.cdr.detectChanges();
            },
        });
    }

    loadCategories() {
        // Load categories for label mapping
        this.categoryService.getPlanningCategories().subscribe({
            next: (data) => {
                this.planningCategoriesMap = data.cats;
                // Flatten for easy lookup
                this.allCategories = [];
                Object.values(this.planningCategoriesMap).forEach((catList: any) => {
                    this.allCategories.push(...catList);
                });
                // Once categories are loaded, fetch campaigns to ensure we can map labels
                this.fetchCampaigns();
            },
            error: (err) => {
                console.error('Error loading categories', err);
                this.fetchCampaigns(); // Fetch anyway
            }
        });
    }

    fetchCampaigns() {
        this.selectedCampaignIds.clear(); // Reset selection on refresh
        // Fetch real campaigns from backend
        const start = startOfDay(subDays(this.viewDate, 30)).toISOString().split('T')[0];
        const end = endOfDay(addDays(this.viewDate, 30)).toISOString().split('T')[0];

        this.campaignService.getScheduledCampaigns(start, end).subscribe({
            next: (campaigns: ScheduledCampaign[]) => {
                this.events = campaigns.map(c => this.campaignToEvent(c));
                this.updateWeekDays();
                this.rebuildCampaignsByDay();
                this.refresh.next();
                this.cdr.markForCheck();
            },
            error: (err) => {
                console.error('Error fetching campaigns', err);
            }
        });
    }

    private campaignToEvent(c: ScheduledCampaign): CalendarEvent {
        let segmentsData: any[] = [];
        let savedStats: any = null;

        if (c.segments && (c.segments as any).audiences) {
            segmentsData = (c.segments as any).audiences;
            savedStats = (c.segments as any).stats;
        } else if (Array.isArray(c.segments)) {
            segmentsData = c.segments;
        }

        let totalUsers = 0;
        let avgLift = 0;
        let avgPotential = 0;
        const catLabels: { label: string; metaIndex: number }[] = [];
        const metaIds = new Set<number>();

        // Flatten nested audiences: [[{category:1},{category:2}],[{category:3}]] → [{category:1},{category:2},{category:3}]
        const flatSegments = segmentsData.flat();

        flatSegments.forEach((s: any) => {
            const catId = s.category;
            const catObj = this.allCategories.find(cat => cat.id === catId && cat.meta_category === s.meta_category)
                || this.allCategories.find(cat => cat.id === catId);
            if (catObj) {
                metaIds.add(s.meta_category);
                const metaIndex = [...metaIds].indexOf(s.meta_category);
                catLabels.push({ label: catObj.label, metaIndex });
            }
        });

        if (savedStats) {
            totalUsers = savedStats.numb_mail;
            avgLift = savedStats.lift;
            avgPotential = savedStats.potential;
        } else {
            let totalLift = 0, totalPotential = 0, count = 0;
            flatSegments.forEach((s: any) => {
                totalUsers += s.mailCount || 0;
                totalLift += s.lift || 0;
                totalPotential += s.potential || 0;
                count++;
            });
            avgLift = count > 0 ? (totalLift / count) : 0;
            avgPotential = count > 0 ? (totalPotential / count) : 0;
        }

        return {
            start: new Date(c.date),
            title: c.name,
            color: DEEBR_COLORS[(c.id || 0) % DEEBR_COLORS.length],
            allDay: true,
            meta: {
                ...c,
                stats: {
                    userCount: totalUsers,
                    lift: Number(avgLift).toFixed(1),
                    potential: Number(avgPotential).toFixed(1),
                    categories: catLabels.map(c => c.label).join(', '),
                    categoryList: catLabels
                }
            }
        };
    }

    onCampaignSaved(campaign: ScheduledCampaign) {
        const event = this.campaignToEvent(campaign);
        const idx = this.events.findIndex(e => e.meta?.id === campaign.id);
        if (idx >= 0) {
            this.events[idx] = event;
        } else {
            this.events = [...this.events, event];
        }
        this.rebuildCampaignsByDay();
        this.cdr.markForCheck();
    }

    updateWeekDays() {
        const startOfWeekDate = startOfDay(subDays(this.viewDate, this.viewDate.getDay() === 0 ? 6 : this.viewDate.getDay() - 1)); // Monday
        this.weekDays = [];
        for (let i = 0; i < 7; i++) {
            this.weekDays.push(addDays(startOfWeekDate, i));
        }
    }

    // Cached campaigns per day key (yyyy-MM-dd) to avoid recreating arrays in template
    campaignsByDay: Map<string, CalendarEvent[]> = new Map();

    private rebuildCampaignsByDay(): void {
        this.campaignsByDay = new Map();
        for (const day of this.weekDays) {
            const key = format(day, 'yyyy-MM-dd');
            this.campaignsByDay.set(key, this.events.filter(event => isSameDay(event.start, day)));
        }
    }

    getCampaignsForDay(date: Date): CalendarEvent[] {
        const key = format(date, 'yyyy-MM-dd');
        return this.campaignsByDay.get(key) || [];
    }

    trackByEventId(_index: number, event: CalendarEvent): number {
        return event.meta?.id ?? _index;
    }

    dayClicked({ date, events }: { date: Date; events: CalendarEvent[] }): void {
        this.openSidebar(date);
    }

    openSidebar(date: Date, event?: CalendarEvent) {
        this.selectedDate = date;
        if (event && event.meta) {
            console.log('Opening sidebar for EDIT:', event.meta);
            this.selectedCampaign = event.meta;
        } else {
            console.log('Opening sidebar for CREATE');
            this.selectedCampaign = null;
        }
        this.sidebarOpen = true;
    }

    private isClosing = false;

    closeSidebar() {
        if (this.isClosing) return;
        if (this.campaignSidebarRef?.hasUnsavedChanges) {
            this.isClosing = true;
            this.dialogService.open(this.unsavedChangesDialog).onClose.subscribe((confirmed) => {
                console.log('[closeSidebar] dialog onClose, confirmed=', confirmed, typeof confirmed);
                this.isClosing = false;
                if (confirmed) {
                    this.sidebarOpen = false;
                    this.fetchCampaigns();
                }
            });
        } else {
            this.sidebarOpen = false;
            this.fetchCampaigns();
        }
    }

    setView(view: CalendarView) {
        this.view = view;
        this.updateWeekDays();
    }

    // Check if viewDate has changed (e.g. via Next/Prev buttons) to update week days
    onViewDateChange() {
        this.updateWeekDays();
        this.rebuildCampaignsByDay();
        this.closeOpenMonthViewDay();
    }

    onCalendarDatePick(date: Date) {
        this.viewDate = date;
        this.updateWeekDays();
        this.rebuildCampaignsByDay();
        this.cdr.detectChanges();
        this.fetchCampaigns();
    }

    closeOpenMonthViewDay() {
        this.activeDayIsOpen = false;
    }

    toggleSelection(id: number) {
        const newSet = new Set(this.selectedCampaignIds);
        if (newSet.has(id)) {
            newSet.delete(id);
        } else {
            newSet.add(id);
        }
        this.selectedCampaignIds = newSet;
    }

    openOptimizeDialog() {
        const selectedEvents = this.events.filter(e => this.selectedCampaignIds.has(e.meta?.id));
        if (selectedEvents.length < 2) return;

        // Auto-include campaigns linked via capped_with (transitive closure)
        const includedIds = new Set(this.selectedCampaignIds);
        const autoIncludedIds = new Set<number>();
        let changed = true;
        while (changed) {
            changed = false;
            for (const event of this.events) {
                const id = event.meta?.id;
                if (!id || includedIds.has(id)) continue;
                const cappedWith: number[] = event.meta?.segments?.capping?.capped_with || [];
                if (cappedWith.some(linkedId => includedIds.has(linkedId))) {
                    includedIds.add(id);
                    autoIncludedIds.add(id);
                    changed = true;
                }
            }
            // Also check forward links from already included campaigns
            for (const event of this.events) {
                const id = event.meta?.id;
                if (!id || !includedIds.has(id)) continue;
                const cappedWith: number[] = event.meta?.segments?.capping?.capped_with || [];
                for (const linkedId of cappedWith) {
                    if (!includedIds.has(linkedId)) {
                        includedIds.add(linkedId);
                        autoIncludedIds.add(linkedId);
                        changed = true;
                    }
                }
            }
        }

        const allEvents = this.events.filter(e => includedIds.has(e.meta?.id));

        this.dialogService.open(OptimizeDialogComponent, {
            context: {
                campaigns: allEvents,
                autoIncludedIds: autoIncludedIds,
            },
            closeOnBackdropClick: false,
        }).onClose.subscribe((result: any) => {
            if (result?.applied) {
                this.fetchCampaigns();
            }
        });
    }

    downloadSelected() {
        const ids = Array.from(this.selectedCampaignIds);
        if (ids.length === 0) return;

        if (this.pushMode) {
            this.pushSelected(ids);
            return;
        }

        this.isDownloading = true;
        this.cdr.detectChanges();
        this.campaignService.downloadScheduledCampaigns(ids, 100).subscribe({
            next: (blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `campaigns_export_${new Date().toISOString().slice(0, 10)}.zip`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                this.selectedCampaignIds.clear();
                this.isDownloading = false;
                this.cdr.detectChanges();
            },
            error: (err) => {
                console.error('Download failed', err);
                this.isDownloading = false;
                this.cdr.detectChanges();
            }
        });
    }

    private pushSelected(ids: number[]) {
        this.isDownloading = true;
        this.cdr.detectChanges();
        this.campaignService.pushScheduledCampaigns(ids, 100).subscribe({
            next: (res) => {
                const success = res.results.filter((r) => r.status === 'success');
                const failed = res.results.filter((r) => r.status === 'failed');
                if (success.length > 0) {
                    const details = success.map((r) => `${r.list_name} (${r.contacts} contacts)`).join(', ');
                    this.toastrService.success(details, `${success.length} liste(s) créée(s) dans l'outil mailing`, { duration: 8000 });
                }
                for (const r of failed) {
                    this.toastrService.danger(r.message || 'Erreur inconnue', `Échec pour ${r.campaign}`, { duration: 0 });
                }
                this.selectedCampaignIds.clear();
                this.isDownloading = false;
                this.cdr.detectChanges();
            },
            error: (err) => {
                console.error('Push failed', err);
                const results = err?.error?.results;
                const message = results?.map((r: any) => r.message).filter(Boolean).join(' / ')
                    || err?.error?.error
                    || 'Erreur lors de la création des listes';
                this.toastrService.danger(message, 'Échec du push', { duration: 0 });
                this.isDownloading = false;
                this.cdr.detectChanges();
            }
        });
    }

    deleteSelected() {
        const ids = Array.from(this.selectedCampaignIds);
        if (ids.length === 0) return;

        this.dialogService.open(this.deleteConfirmDialog).onClose.subscribe((confirmed: boolean) => {
            if (!confirmed) return;

            this.isDeleting = true;
            this.cdr.detectChanges();

            let completed = 0;
            let hasError = false;
            ids.forEach(id => {
                this.campaignService.deleteScheduledCampaign(id).subscribe({
                    next: () => {
                        completed++;
                        // Remove card as soon as its delete succeeds
                        this.events = this.events.filter(e => e.meta?.id !== id);
                        this.selectedCampaignIds.delete(id);
                        this.rebuildCampaignsByDay();
                        this.cdr.detectChanges();
                        if (completed === ids.length) {
                            this.isDeleting = false;
                            this.cdr.detectChanges();
                        }
                    },
                    error: (err) => {
                        console.error('Delete failed', err);
                        completed++;
                        hasError = true;
                        if (completed === ids.length) {
                            this.isDeleting = false;
                            if (hasError) this.fetchCampaigns();
                        }
                    }
                });
            });
        });
    }

    onCardMouseEnter(id: number) {
        if (this.isDragging) return;
        this.hoveredEventId = id;
        this.hoveredLinkedIds = this.computeLinkedIds(id);
        this.cdr.markForCheck();
    }

    onCardMouseLeave() {
        if (this.isDragging) return;
        this.hoveredEventId = null;
        this.hoveredLinkedIds = new Set();
        this.cdr.markForCheck();
    }

    onDragStarted(): void {
        this.isDragging = true;
        this.hoveredEventId = null;
        this.hoveredLinkedIds = new Set();
        document.body.classList.add('cdk-dragging');
    }

    onDragEnded(): void {
        this.isDragging = false;
        document.body.classList.remove('cdk-dragging');
    }

    startTour(): void {
        // Inject a demo campaign if the calendar is empty so tour steps have something to point at
        let demoInjected = false;
        if (this.events.length === 0) {
            const today = this.weekDays.find(d => isSameDay(d, new Date())) || this.weekDays[2]; // Wed fallback
            this.events = [{
                start: today,
                title: 'Campagne démo',
                color: DEEBR_COLORS[1],
                allDay: true,
                meta: {
                    id: -1,
                    name: 'Campagne démo',
                    date: today.toISOString().split('T')[0],
                    segments: { audiences: [] },
                    status: 'DRAFT',
                    stats: {
                        userCount: 12500,
                        lift: '2.3',
                        potential: '18.5',
                        categories: 'Catégorie exemple',
                        categoryList: [{ label: 'Catégorie exemple', metaIndex: 0 }]
                    }
                }
            }];
            demoInjected = true;
            this.rebuildCampaignsByDay();
            this.cdr.detectChanges();
        }

        const removeDemoIfNeeded = () => {
            if (demoInjected) {
                this.events = this.events.filter(e => e.meta?.id !== -1);
                this.rebuildCampaignsByDay();
                this.cdr.detectChanges();
            }
        };

        const steps: TourStep[] = [
            {
                id: 'welcome',
                title: 'Bienvenue sur le Planning',
                text: 'Cette page vous permet de <b>planifier, configurer et exporter</b> vos campagnes sur la semaine. Découvrons les fonctionnalités.',
            },
            {
                id: 'navigation',
                title: 'Navigation',
                text: 'Naviguez entre les semaines avec <b>Précédent</b> / <b>Suivant</b>, ou revenez à la semaine courante avec <b>Aujourd\'hui</b>.',
                attachTo: { element: '[data-tour="navigation"]', on: 'bottom' },
            },
            {
                id: 'calendar',
                title: 'Vue calendrier',
                text: 'Vos campagnes sont organisées par <b>jour de la semaine</b> sous forme de cartes dans chaque colonne.',
                attachTo: { element: '[data-tour="calendar"]', on: 'top' },
            },
            {
                id: 'create-card',
                title: 'Créer une campagne',
                text: 'Survolez une colonne pour faire apparaître le bouton <b>+</b>, puis cliquez dessus pour créer une campagne sur ce jour.',
                attachTo: { element: '[data-tour="create-card"]', on: 'bottom' },
                beforeShowPromise: () => new Promise<void>(resolve => {
                    // Force the create placeholder to be visible during this step
                    const el = document.querySelector<HTMLElement>('[data-tour="create-card"]');
                    if (el) el.style.display = 'flex';
                    resolve();
                }),
            },
            {
                id: 'sidebar',
                title: 'Configuration de campagne',
                text: 'Un panneau plein écran s\'ouvre avec :<br><br>• <b>Segments</b> — choisissez les catégories et audiences ciblées<br>• <b>Filtres CRM</b> — affinez la cible (ex : ancienneté, fréquence d\'achat…)<br>• <b>Pression</b> — ajustez le % de la base à cibler via le slider<br>• <b>Courbe de lift</b> — visualisez le gain de performance en fonction de la pression choisie',
            },
            {
                id: 'campaign-card',
                title: 'Carte campagne',
                text: 'Chaque carte affiche le <b>nom</b>, les <b>catégories</b>, le nombre d\'<b>utilisateurs</b>, le <b>lift</b> et le <b>potentiel</b>.<br><br>Cliquez dessus pour modifier la configuration.',
                attachTo: { element: '.kanban-card', on: 'bottom' },
            },
            {
                id: 'drag-drop',
                title: 'Glisser-déposer',
                text: 'Vous pouvez <b>déplacer une campagne</b> d\'un jour à l\'autre en la glissant avec la souris vers la colonne souhaitée. La date sera automatiquement mise à jour.',
                attachTo: { element: '.kanban-card', on: 'right' },
            },
            {
                id: 'selection',
                title: 'Sélection multiple',
                text: 'Survolez une carte et cochez la <b>case en haut à droite</b> pour la sélectionner.<br>Vous pouvez en sélectionner plusieurs.',
                attachTo: { element: '.kanban-card', on: 'right' },
            },
            {
                id: 'actions-and-links',
                title: 'Exporter, Optimiser & Campagnes liées',
                text: 'Les boutons en haut à droite s\'activent lorsque vous sélectionnez des campagnes :<br><br>• <b>Supprimer</b> — supprime les campagnes sélectionnées<br>• <b>Exporter</b> — génère un fichier avec les emails des segments<br>• <b>Optimiser</b> (2+ campagnes) — ajuste la pression pour <b>limiter les doublons</b> entre audiences<br><br>Après optimisation, les campagnes deviennent <b>liées</b> : au survol, elles s\'affichent en <b style="color:#ffd877">jaune</b> et les autres sont atténuées.',
                attachTo: { element: '[data-tour="actions"]', on: 'bottom' },
            },
        ];

        this.tourService.start(PlanningCampaignComponent.TOUR_KEY, steps, false, removeDemoIfNeeded);
    }

    onDrop(event: CdkDragDrop<Date, Date, CalendarEvent>): void {
        const droppedEvent = event.item.data;
        const newDay: Date = event.container.data;

        // Skip if dropped on the same day
        if (isSameDay(droppedEvent.start, newDay)) return;

        const campaignId = droppedEvent.meta?.id;
        if (!campaignId || campaignId === -1) return;

        const newDate = format(newDay, 'yyyy-MM-dd');

        // Optimistic update
        droppedEvent.start = newDay;
        droppedEvent.meta.date = newDate;
        this.rebuildCampaignsByDay();
        this.cdr.markForCheck();

        this.campaignService.patchScheduledCampaign(campaignId, { date: newDate } as any).subscribe({
            next: () => { /* success — already updated locally */ },
            error: (err) => {
                console.error('Failed to move campaign', err);
                this.fetchCampaigns(); // revert by reloading
            }
        });
    }

    private autoStartTour(): void {
        if (this.tourService.isCompleted(PlanningCampaignComponent.TOUR_KEY)) return;
        // Delay to let the UI render before attaching tour steps
        setTimeout(() => this.startTour(), 1000);
    }

    private computeLinkedIds(hoveredId: number): Set<number> {
        const result = new Set<number>();
        // Forward: campaigns listed in the hovered campaign's capped_with
        const hoveredEvent = this.events.find(e => e.meta?.id === hoveredId);
        const cappedWith: number[] = hoveredEvent?.meta?.segments?.capping?.capped_with || [];
        cappedWith.forEach(id => result.add(id));
        // Reverse: campaigns that include the hovered campaign in their capped_with
        this.events.forEach(e => {
            if (e.meta?.id === hoveredId) return;
            const cp: number[] = e.meta?.segments?.capping?.capped_with || [];
            if (cp.includes(hoveredId)) result.add(e.meta?.id);
        });
        return result;
    }
}
